/*
 * Rebuy Smart Cart — AJAX enroll / add-to-cart bridge
 * ---------------------------------------------------
 * Goal: when a visitor clicks an "Enroll" / add-to-cart CTA we want to
 *   1. NOT do a full-page redirect to /cart or ?viewcart=true,
 *   2. add the course via the Shopify AJAX Cart API,
 *   3. block adding the SAME course/variant twice (keep qty at 1),
 *   4. open the Rebuy Smart Cart drawer in place, and
 *   5. keep the theme cart-count badge in sync.
 *
 * Two cooperating layers:
 *
 *   A. Click take-over (capture phase, runs before Replo/theme handlers).
 *      For CTAs whose variant we can resolve with confidence, we fully own the
 *      click: prevent the default/redirect, dedupe, AJAX-add, open Rebuy.
 *
 *   B. Network safety-net (patches fetch + XHR, mirrors cart-quantity-limit.js).
 *      For any /cart/add that we DIDN'T take over (Replo's own multi-product
 *      buttons, the theme's product forms, permalinks, etc.) we still: clamp
 *      course variants back to qty 1 (dedupe), refresh the count, and open Rebuy.
 *
 * CTAs we deliberately DO NOT touch:
 *   - FAQ text auto-linkified as http://No.No
 *   - Discounted express-checkout permalinks:
 *       /cart/<variant>:1?discount=...&checkout[redirect_url]=/checkout
 *   - mailto: links
 *
 * "Course" products = every product Replo injects into #replo-deps-products.
 * Those are the ones capped at 1 per cart.
 */
(function () {
  'use strict';

  // Run once. If the asset is included twice we would register two capture-phase
  // click listeners and run handleEnroll twice per click (two identical
  // "[rebuy] selected variant" lines showed up in the Safari console).
  if (window.__rbSmartCartInit) return;
  window.__rbSmartCartInit = true;

  // Diagnostic kill-switch: load any page with ?rbdisable=1 to take this file
  // out of the picture entirely. It patches window.fetch and XHR before the app
  // scripts run, so it is the first thing to rule in or out when a third-party
  // bundle fails to load in one browser but not another.
  try {
    if (/[?&]rbdisable=1/.test(location.search)) {
      window.RB_DISABLED = true;
      return;
    }
  } catch (e) {}

  // Build marker — bump this on every deploy so we can prove, from the browser,
  // exactly which build of this file the live theme is actually serving.
  var RB_BUILD = 'RB-BUILD-2026-07-28-XSELL-DISCOUNT';
  window.RB_BUILD = RB_BUILD;

  // --- TEMPORARY DIAGNOSTIC (remove once the Safari issue is closed) ----------
  // Rebuy's Smart Cart is a Vue app; on the Replo workshop page in Safari it
  // half-initialises (window.Rebuy exists with only `customer`, no SmartCart),
  // which is the signature of a script error during its boot. This file runs
  // before the app scripts, so it can capture those errors for inspection via
  // window.__rbErrors — reading them needs no devtools filtering.
  window.__rbErrors = window.__rbErrors || [];
  window.addEventListener('error', function (e) {
    try {
      window.__rbErrors.push({
        kind: e.message ? 'error' : 'resource',
        message: e.message || ('failed to load: ' + ((e.target && (e.target.src || e.target.href)) || '?')),
        source: e.filename || ((e.target && (e.target.src || e.target.href)) || ''),
        line: e.lineno || 0,
        stack: (e.error && e.error.stack) ? String(e.error.stack).slice(0, 400) : ''
      });
    } catch (err) {}
  }, true); // capture: also catches failed <script>/<img> loads, which don't bubble
  window.addEventListener('unhandledrejection', function (e) {
    try {
      var r = e.reason;
      window.__rbErrors.push({
        kind: 'promise',
        message: (r && (r.message || r.toString && r.toString())) || 'unhandled rejection',
        stack: (r && r.stack) ? String(r.stack).slice(0, 400) : ''
      });
    } catch (err) {}
  });
  // --- end temporary diagnostic ----------------------------------------------

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var INTERNAL_HEADER = 'X-Rebuy-Ajax';           // marks our own requests so the net-layer ignores them
  var DUPLICATE_MSG = 'This course is already in your cart.';
  var DRAWER_TITLE = 'Your Enrollments';   // matches the Rebuy cart it replaces
  var RB_DEBUG = true; // set to false once the variant matching is confirmed in the browser console

  // ---------------------------------------------------------------------------
  // Small AJAX helpers. All of OUR requests carry INTERNAL_HEADER so the
  // fetch/XHR interceptors below skip them (no recursion / double handling).
  // ---------------------------------------------------------------------------
  function ajax(url, init) {
    init = init || {};
    init.headers = Object.assign({ Accept: 'application/json' }, init.headers || {});
    init.headers[INTERNAL_HEADER] = '1';
    init.credentials = 'same-origin';
    return fetch(url, init);
  }
  function getCart() {
    // no-store: mobile browsers can hand back a cached /cart.js, which made us
    // think a course was in/out of the cart when the real cart said otherwise.
    return ajax(root + 'cart.js', { cache: 'no-store' }).then(function (r) { return r.json(); });
  }
  function addVariant(id, qty) {
    return ajax(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: id, quantity: qty || 1 })
    }).then(function (r) { return r.json(); });
  }
  // Apply a discount code without leaving the page.
  //
  // Shopify has no AJAX endpoint for discount codes; the supported way is to hit
  // /discount/<code>, which sets the discount cookie and then redirects. Sending
  // it to /cart.js keeps that redirect cheap (small JSON instead of the whole
  // cart page) and hands back the fresh cart. The code is only stored at this
  // point — the saving itself is calculated at checkout.
  function applyDiscount(code) {
    if (!code) return Promise.resolve(null);
    return ajax(root + 'discount/' + encodeURIComponent(code) + '?redirect=/cart.js',
                { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });   // never let this break add-to-cart
  }

  function discountOn(el) {
    var host = el && el.closest && el.closest('[data-discount-code]');
    var code = host && host.getAttribute('data-discount-code');
    code = (code || '').trim();
    if (code) return code;
    // Cross-sell "Add" buttons carry no discount code of their own, but the
    // upsell must get the SAME discount as the page's enrollment. Fall back to
    // the code declared on the page's enroll button so adding an upsell applies
    // it too (the cart-level discount then covers every eligible line).
    var any = document.querySelector('[data-discount-code]');
    var anyCode = any && any.getAttribute('data-discount-code');
    return (anyCode || '').trim();
  }

  function updateLine(key, qty) {
    var updates = {}; updates[key] = qty;
    return ajax(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (r) { return r.json(); });
  }

  // ---------------------------------------------------------------------------
  // Product / variant resolution from Replo's injected product JSON.
  // ---------------------------------------------------------------------------
  function collectProducts() {
    var out = [];
    // Multiple chunks can each emit an (id-duplicated) script — grab them all.
    var scripts = document.querySelectorAll('script[id="replo-deps-products"]');
    for (var i = 0; i < scripts.length; i++) {
      try {
        var arr = JSON.parse(scripts[i].textContent);
        (arr || []).forEach(function (p) {
          if (p && p.data && Array.isArray(p.data.variants)) out.push(p.data);
        });
      } catch (e) { /* ignore malformed */ }
    }
    return out;
  }

  // Set of every variant id that belongs to a course product (capped at 1).
  function courseVariantSet() {
    var set = {};
    collectProducts().forEach(function (p) {
      (p.variants || []).forEach(function (v) { set[v.id] = true; });
    });
    return set;
  }

  function firstAvailableVariantId(product) {
    var variants = product.variants || [];
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].available) return variants[i].id;
    }
    return variants.length ? variants[0].id : null;
  }

  // Read the selected variant id STRICTLY from the product form the trigger
  // belongs to. No page-wide fallback: a global [name="id"] query would grab a
  // Rebuy cross-sell widget's form and add the WRONG product (that was the bug).
  function variantFromForm(trigger) {
    var form = trigger.closest && trigger.closest('form');
    if (!form) return null;
    var controls = Array.prototype.slice.call(form.querySelectorAll('[name="id"]'));
    if (form.id) {
      // Controls linked to THIS form via form="<id>" (e.g. the variant <select>).
      controls = controls.concat(Array.prototype.slice.call(
        document.querySelectorAll('[name="id"][form="' + form.id + '"]')));
    }
    for (var i = 0; i < controls.length; i++) {
      var el = controls[i];
      if (el.disabled || !el.value) continue;   // Shopify disables the unused hidden input
      var id = parseInt(el.value, 10);
      if (id) return id;
    }
    return null;
  }

  // Returns a numeric variant id, or null when we can't confidently pick one
  // (e.g. a page selling multiple different courses at once).
  function resolveVariant(trigger) {
    // 1. Explicit attribute, if a theme/dev ever adds one.
    var withAttr = trigger.closest && trigger.closest('[data-variant-id]');
    if (withAttr) {
      var idAttr = parseInt(withAttr.getAttribute('data-variant-id'), 10);
      if (idAttr) return idAttr;
    }
    // 2. The product form's selected variant (product pages / sticky bar).
    var fromForm = variantFromForm(trigger);
    if (fromForm) return fromForm;
    // 3. Single-product page -> its first available variant (the common case).
    var products = collectProducts();
    if (products.length === 1) return firstAvailableVariantId(products[0]);
    return null; // ambiguous -> defer to Replo + the network safety-net
  }

  // ---------------------------------------------------------------------------
  // Rebuy Smart Cart control. The exact API differs across Rebuy versions, so we
  // try the documented methods first and fall back to toggling the DOM state
  // that the drawer itself uses. If Rebuy has not booted yet we retry.
  // ---------------------------------------------------------------------------
  // Rebuy loads its Smart Cart lazily: the bundle exposes importSmartCartModule()
  // and only then does Rebuy.SmartCart exist. In Chrome something triggers that
  // import on its own; in Safari it never fired, so we sat waiting for a
  // Rebuy.SmartCart that was never going to appear — the whole "Safari doesn't
  // open the drawer" problem. Ask for the module ourselves instead of waiting.
  var scImportTried = false;
  function ensureSmartCart() {
    try {
      var R = window.Rebuy;
      if (!R) return;
      if (R.SmartCart && typeof R.SmartCart.show === 'function') return;
      if (scImportTried || typeof R.importSmartCartModule !== 'function') return;
      scImportTried = true;
      var p = R.importSmartCartModule();
      if (p && typeof p.then === 'function') {
        // Let a failed import be retried on the next attempt.
        p.catch(function () { scImportTried = false; });
      }
      if (typeof R.fetchSmartCartData === 'function') {
        try { R.fetchSmartCartData(); } catch (e) {}
      }
    } catch (e) { scImportTried = false; }
  }

  function tryRebuyOpen() {
    try {
      ensureSmartCart();
      var R = window.Rebuy;
      if (R && R.SmartCart) {
        if (typeof R.SmartCart.refresh === 'function') { try { R.SmartCart.refresh(); } catch (e) {} }
        if (typeof R.SmartCart.show === 'function') { R.SmartCart.show(); return true; }
        if (typeof R.SmartCart.open === 'function') { R.SmartCart.open(); return true; }
        if (typeof R.SmartCart.toggle === 'function') { R.SmartCart.toggle(); return true; }
      }
      if (R && R.Cart && typeof R.Cart.open === 'function') { R.Cart.open(); return true; }
    } catch (e) { /* fall through to DOM fallback */ }
    return false;
  }

  // Mirror the open state we observed on the live drawer:
  // ---------------------------------------------------------------------------
  // The theme's own mini-cart drawer (snippets/side-drawer.liquid, driven by
  // cart-ajax.js). Preferred over Rebuy: it ships with the theme, so it can't
  // fail to load the way a third-party bundle can, and Rebuy's own drawer is
  // hidden in theme.liquid. These are the steps cart-ajax.js runs on a success.
  // ---------------------------------------------------------------------------
  function themeDrawerEl() {
    return document.querySelector('[data-side-drawer]');
  }
  // Re-render the theme drawer's contents from the live cart. buildCart is a
  // global in cart-ajax.js (defined outside the cart-drawer setting guard), so
  // this works even when that setting is off. Passing false leaves the open
  // state alone — we manage that ourselves.
  // Shopify's dynamic checkout buttons (Shop Pay etc.) are a web component that
  // ships un-hydrated when the drawer HTML is injected over AJAX. Re-run the
  // theme's initialiser once it appears. Polls briefly because buildCart loads
  // the markup asynchronously.
  function initPaymentButtons() {
    var tries = 0;
    (function again() {
      var el = document.querySelector('[data-side-drawer] .cart-drawer-dynamic-checkout');
      if (el) {
        try {
          if (window.Shopify && Shopify.PaymentButton && typeof Shopify.PaymentButton.init === 'function') {
            Shopify.PaymentButton.init();
          }
        } catch (e) {}
      }
      if (++tries < 12) setTimeout(again, 250); // ~3s
    })();
  }

  function renderThemeDrawer(knownCart) {
    var p = knownCart ? Promise.resolve(knownCart) : getCart();
    return p.then(function (cart) {
      try { if (typeof window.buildCart === 'function') window.buildCart(cart, false); } catch (e) {}
      updateCount(cart);
      initPaymentButtons();
      return cart;
    }).catch(function () {});
  }

  function themeDrawerHasItems() {
    var d = themeDrawerEl();
    return !!(d && d.querySelector('.media-link'));
  }
  function themeDrawerIsOpen() {
    return !!document.body && document.body.classList.contains('side_Drawer_open');
  }
  // Style the drawer as our mini-cart but leave it CLOSED, so we can load its
  // contents before it slides in (no empty-drawer flash).
  function prepareThemeDrawer() {
    var el = themeDrawerEl();
    if (!el) return false;
    try {
      el.className = 'yv_side_drawer_wrapper mini_cart';
      el.id = 'mini__cart';
      var heading = el.querySelector('[data-drawer-title]');
      if (heading) heading.textContent = DRAWER_TITLE;
      return true;
    } catch (e) { return false; }
  }
  // Slide it in.
  function revealThemeDrawer() {
    if (!document.body) return;
    document.body.classList.remove('quickview-open');
    document.body.classList.add('side_Drawer_open');
  }
  // prepare + reveal + render in one go (used by the network safety-net etc.)
  function openThemeDrawer() {
    if (!prepareThemeDrawer()) return false;
    revealThemeDrawer();
    renderThemeDrawer();
    return true;
  }
  // Open the drawer only once the product is actually in it, then stop the
  // button spinner. A hard cap opens it anyway so it can never hang.
  function revealDrawerWhenReady(trigger) {
    var tries = 0, max = 200;   // ~16s cap
    (function again() {
      if (themeDrawerHasItems() || ++tries >= max) {
        revealThemeDrawer();
        setBusy(trigger, false);
        return;
      }
      setTimeout(again, 80);
    })();
  }

  //   <div id="rebuy-cart" class="rebuy-cart is-visible ..." aria-hidden="false">
  function domOpenFallback() {
    var el = document.getElementById('rebuy-cart');
    if (!el) return false;
    el.setAttribute('aria-hidden', 'false');
    el.classList.add('is-visible');
    document.documentElement.classList.add('rebuy-cart-visible');
    if (document.body) document.body.classList.add('rebuy-cart-visible');
    return true;
  }

  // Undo exactly what domOpenFallback() applied. Without this, the html/body
  // `rebuy-cart-visible` class we added survives Rebuy's own close (Rebuy only
  // clears state it set itself) and the drawer could no longer be dismissed —
  // the CSS that actually reveals it is `.rebuy-cart-visible .rebuy-cart__background`.
  function domCloseFallback() {
    var el = document.getElementById('rebuy-cart');
    if (el) {
      el.classList.remove('is-visible');
      el.setAttribute('aria-hidden', 'true');
    }
    document.documentElement.classList.remove('rebuy-cart-visible');
    if (document.body) document.body.classList.remove('rebuy-cart-visible');
  }

  // Is the drawer actually on screen? Trust the DOM, not the return value of
  // show() — on mobile Rebuy.SmartCart.show() reports success but never opens
  // anything, so the drawer stays shut.
  //
  // aria-hidden must NOT be tested: Rebuy leaves aria-hidden="false" on
  // #rebuy-cart while the drawer is CLOSED, and treating that as "open" made this
  // return true immediately so the fallback never ran (the original iOS bug).
  // We check both states that can reveal it: Rebuy's own is-visible, and the
  // html-level class the CSS keys the backdrop off.
  function drawerIsOpen() {
    if (themeDrawerIsOpen()) return true;
    var el = document.getElementById('rebuy-cart');
    if (el && el.classList.contains('is-visible')) return true;
    return document.documentElement.classList.contains('rebuy-cart-visible');
  }

  function openRebuy() {
    // 60ms polling, not 200ms: on a cold boot the drawer should appear the moment
    // Rebuy exists, not a fifth of a second later. ~13s of total runway.
    var tries = 0, max = 215;
    // Theme drawer first — always present, and Rebuy's own drawer is hidden.
    if (openThemeDrawer()) return;
    (function attempt() {
      tryRebuyOpen();               // ask Rebuy's own API to open (desktop path)
      if (drawerIsOpen()) return;   // it really opened — done
      // Rebuy's API said "ok" but the drawer isn't visible (the mobile no-op).
      // Force the open state Rebuy itself uses so it appears on mobile too.
      domOpenFallback();
      if (drawerIsOpen()) return;
      if (++tries >= max) return;   // #rebuy-cart not booted yet — give up quietly
      setTimeout(attempt, 60);
    })();
  }

  // Hold the drawer open after a click ("open intent").
  //
  // Rebuy can finish booting AFTER our click and reset the flyout to closed, so a
  // fast first click looked like nothing happened while a slow one worked — the
  // giveaway was that pasting a diagnostic into the console first (10s+ of delay)
  // made it work every time. A short window missed that late reset, so we hold the
  // intent for several seconds and keep re-asserting.
  //
  // The shopper is never trapped: clicking Rebuy's own close button or backdrop
  // drops the intent immediately (see the cancel listener below).
  var openIntentUntil = 0;

  // 12s, not 6s: on a cold/incognito first visit nothing is cached, so Rebuy can
  // take several seconds to boot and create #rebuy-cart. The first click used to
  // fall outside the old window (the element did not exist yet, so there was
  // nothing to open), while a second click worked because Rebuy was ready by then.
  // The loop below no-ops once the drawer is open and a shopper close cancels it,
  // so a longer window costs nothing.
  function holdDrawerOpen(ms) {
    openIntentUntil = Date.now() + (ms || 12000);
    (function again() {
      if (Date.now() > openIntentUntil) return;
      if (!drawerIsOpen()) {
        tryRebuyOpen();                              // keep Rebuy's own state in sync
        if (!drawerIsOpen()) domOpenFallback();      // and force it if that no-ops
      }
      setTimeout(again, 200);
    })();
  }

  // A real close by the shopper wins immediately and permanently: drop the intent
  // AND strip the state we added, otherwise our html/body class keeps the drawer
  // on screen after Rebuy has closed its own and it can never be dismissed.
  // Close the THEME drawer and restore page scroll. The theme's own close handler
  // that removes `side_Drawer_open` is behind the cart-drawer setting (off here),
  // so without this the class lingers and body stays overflow:hidden — the page
  // could no longer be scrolled after closing.
  function closeThemeDrawer() {
    var b = document.body, h = document.documentElement;
    if (b) {
      b.classList.remove('side_Drawer_open', 'yv_side_Drawer_open',
                         'quickview-open', 'minicart-open', 'scrollHidden');
      b.style.overflow = '';
      b.style.touchAction = '';
      b.style.position = '';
    }
    if (b) b.classList.remove('rebuy-cart-visible');
    if (h) {
      h.classList.remove('rebuy-cart-visible');
      h.style.overflow = '';
    }
    try { if (window.Rebuy && Rebuy.SmartCart && Rebuy.SmartCart.hide) Rebuy.SmartCart.hide(); } catch (e) {}
    var ov = document.querySelector('.wrapper-overlay');
    if (ov) ov.style.display = 'none';
  }

  function cancelOpenIntent() {
    openIntentUntil = 0;
    domCloseFallback();                    // clear Rebuy's state...
    closeThemeDrawer();                     // ...and the theme drawer's + scroll lock
    setTimeout(function () { domCloseFallback(); closeThemeDrawer(); }, 60);
    setTimeout(function () { domCloseFallback(); closeThemeDrawer(); }, 250);
  }

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('#rebuy-cart-close, .rebuy-cart__flyout-close, .rebuy-cart__background,' +
        ' [data-rebuy-cart-close], .yv_side_drawer_close, .wrapper-overlay')) {
      cancelOpenIntent();
    }
  }, true);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && (drawerIsOpen() || themeDrawerIsOpen())) cancelOpenIntent();
  }, true);

  // Ask Rebuy to re-sync its contents after a cart mutation. Rebuy normally
  // auto-refreshes when it sees a /cart/add go by, but we nudge it to be safe.
  function refreshRebuy() {
    try {
      if (window.Rebuy && Rebuy.SmartCart && typeof Rebuy.SmartCart.refresh === 'function') {
        Rebuy.SmartCart.refresh();
      }
      document.dispatchEvent(new CustomEvent('rebuy:cart.change'));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (e) {}
  }

  // Repopulate the drawer AFTER it is open. On mobile the flyout can mount showing
  // an empty/stale cart (the item is really in the cart — /cart proves it — but
  // Rebuy renders empty), and a single refresh() fired before the drawer mounts is
  // lost. So we nudge Rebuy a few times once it's on screen, trying whatever
  // re-render method this Rebuy build exposes. No cart mutation, no qty change.
  // Rebuy marks the drawer with `has-items` once it has rendered cart contents.
  function drawerHasItems() {
    var el = document.getElementById('rebuy-cart');
    return !!el && el.classList.contains('has-items');
  }

  function resyncRebuyDrawer(expectItems) {
    var tries = 0, max = 130;   // ~13s @ 100ms, matching openRebuy's runway
    (function again() {
      try {
        var R = window.Rebuy && window.Rebuy.SmartCart;
        if (R) {
          if (typeof R.refresh === 'function') R.refresh();
          else if (typeof R.update === 'function') R.update();
          else if (typeof R.reload === 'function') R.reload();
          else if (typeof R.render === 'function') R.render();
        }
        document.dispatchEvent(new CustomEvent('rebuy:cart.change'));
      } catch (e) {}
      // Stop once the contents are showing, rather than after a fixed burst: a
      // short burst finished long before a cold Rebuy was ready, leaving the
      // shopper looking at an empty cart for seconds.
      if (expectItems !== false && drawerHasItems()) return;
      if (expectItems === false && tries >= 3) return;
      if (++tries < max) setTimeout(again, 100);
    })();
  }

  // ---------------------------------------------------------------------------
  // Enroll-button disable state — PER VARIANT.
  // We must NOT disable every enroll button just because the cart is non-empty
  // (that made "Idea to Empire" look enrolled after adding "No-Cost Boss").
  // A button is only disabled when ITS OWN resolved variant is in the cart.
  // ---------------------------------------------------------------------------
  var ENROLL_SELECTORS = '.Sd_addProduct, .add_to_cart, [data-replo-add-product-variant-to-cart]';
  // Every Replo add-to-cart button keeps the "enrolled" dimming but stays
  // clickable, so a repeat click re-opens the drawer instead of being swallowed
  // by a disabled state. handleEnroll still refuses to re-add a variant that is
  // already in the cart (no qty bump). The theme's own enroll button carries this
  // same attribute in product-form.liquid so it behaves identically.
  var ALWAYS_OPEN_SELECTOR = '[data-replo-add-product-variant-to-cart]';
  (function injectDisableStyle() {
    var s = document.createElement('style');
    s.id = 'rb-enroll-disable-style';
    s.textContent = '.rb-enrolled{pointer-events:none!important;opacity:.55!important;cursor:not-allowed!important;}' +
      // :not(.is-loading) keeps the button inert while an add is in flight, so a
      // fast double-click can't race two /cart/add calls past the dedupe check.
      '.rb-enrolled' + ALWAYS_OPEN_SELECTOR +
      '{pointer-events:auto!important;cursor:pointer!important;}';
    (document.head || document.documentElement).appendChild(s);
  })();

  function alwaysOpen(el) {
    return !!(el && el.matches && el.matches(ALWAYS_OPEN_SELECTOR));
  }

  function disableBtn(el, on) {
    if (!el) return;
    // A disabled <button> dispatches no click event at all, whatever the CSS says,
    // so opted-in buttons must never get the property or the ARIA state.
    var keepClickable = alwaysOpen(el);
    el.classList.toggle('rb-enrolled', on);
    if ('disabled' in el && !keepClickable) el.disabled = on;
    el.setAttribute('aria-disabled', (on && !keepClickable) ? 'true' : 'false');
  }

  function cartVariantIds(cart) {
    var ids = [];
    (cart && cart.items || []).forEach(function (it) { ids.push(Number(it.variant_id)); });
    return ids;
  }

  // Disable only the buttons whose variant is already in the cart; enable the rest.
  function syncEnrollButtons(cart) {
    var inCart = cartVariantIds(cart);
    if (RB_DEBUG) console.debug('[rebuy] cart variant ids:', inCart);
    var btns = document.querySelectorAll(ENROLL_SELECTORS);
    for (var i = 0; i < btns.length; i++) {
      var v = resolveVariant(btns[i]);
      disableBtn(btns[i], v != null && inCart.indexOf(Number(v)) > -1);
    }
  }

  // ---------------------------------------------------------------------------
  // Theme cart-count badge ( [data-cart-count], see header-icons.liquid ).
  // ---------------------------------------------------------------------------
  function updateCount(cart) {
    if (!cart) return;
    var n = cart.item_count || 0;
    var badges = document.querySelectorAll('[data-cart-count]');
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = n < 100 ? String(n) : '';
      badges[i].style.display = n > 0 ? '' : 'none';
    }
    syncEnrollButtons(cart); // per-variant enable/disable
    // Show the static checkout/Shop Pay footer only when the cart has items.
    var sf = document.querySelector('[data-rbl-static-footer]');
    if (sf) sf.classList.toggle('is-empty', (cart.item_count || 0) === 0);
  }

  // ---------------------------------------------------------------------------
  // Tiny toast for the "already in cart" message.
  // ---------------------------------------------------------------------------
  function toast(msg) {
    var t = document.getElementById('rb-course-toast');
    if (!t) {
      t = document.createElement('div');
      t.id = 'rb-course-toast';
      t.setAttribute('role', 'status');
      t.style.cssText = 'position:fixed;z-index:2147483647;left:50%;bottom:32px;' +
        'transform:translateX(-50%);background:#111;color:#fff;padding:12px 20px;' +
        'border-radius:8px;font-family:Rubik,sans-serif;font-size:15px;line-height:1.3;' +
        'box-shadow:0 6px 24px rgba(0,0,0,.28);opacity:0;transition:opacity .25s ease;' +
        'max-width:90vw;text-align:center;pointer-events:none;';
      (document.body || document.documentElement).appendChild(t);
    }
    t.textContent = msg;
    requestAnimationFrame(function () { t.style.opacity = '1'; });
    clearTimeout(t._hide);
    t._hide = setTimeout(function () { t.style.opacity = '0'; }, 3200);
  }

  // ---------------------------------------------------------------------------
  // Layer A — we fully own this enroll click.
  // ---------------------------------------------------------------------------
  // Show/hide a lightweight loading state on the clicked button so the click
  // feels instant while we add + prime Rebuy in the background.
  function setBusy(el, on) {
    if (!el) return;
    // Toggle the loading state; the stylesheet hides the label (button keeps its
    // width) and shows a centred, continuously spinning loader.
    try { el.classList.toggle('is-loading', !!on); } catch (e) {}
  }

  // Keep the clicked button spinning until the drawer is actually on screen. On a
  // cold visit Rebuy takes seconds to boot, and clearing the spinner as soon as
  // the cart call returned left the shopper staring at an idle-looking button
  // while nothing had opened. Scoped to the button — the page is never blocked —
  // and it always clears, even if Rebuy never shows up.
  function clearBusyWhenOpen(trigger) {
    var tries = 0, max = 200;   // ~16s hard cap — never leave it spinning forever
    (function again() {
      if (themeDrawerHasItems() || ++tries >= max) { setBusy(trigger, false); return; }
      setTimeout(again, 80);
    })();
  }

  // A click we cannot fully own — the variant is not resolvable yet, which is what
  // happens when someone clicks WHILE the page is still loading and Replo's
  // #replo-deps-products JSON has not parsed. We used to return silently, so those
  // clicks opened nothing at all. Now the click is still honoured: open the drawer
  // and leave the add to Replo/the theme (the network safety-net picks it up).
  // No preventDefault here — their handler must keep working.
  function openUnresolved() {
    openRebuy();
    holdDrawerOpen();
  }

  function handleEnroll(variantId, trigger) {
    var wanted = Number(variantId);
    var code = discountOn(trigger);
    setBusy(trigger, true);                 // spinner on the button

    // If we can't drive the theme drawer (e.g. it isn't on this page), fall back
    // to the old immediate-open behaviour so nothing regresses elsewhere.
    if (!prepareThemeDrawer()) {
      openRebuy(); holdDrawerOpen(); resyncRebuyDrawer(true);
      getCart().then(function (cart) {
        var already = cartVariantIds(cart).indexOf(wanted) > -1;
        if (already) { disableBtn(trigger, true); applyDiscount(code); toast(DUPLICATE_MSG); return; }
        return addVariant(wanted, 1).then(function () { disableBtn(trigger, true); applyDiscount(code); });
      }).catch(function () { openRebuy(); }).then(function () { clearBusyWhenOpen(trigger); });
      return;
    }

    // Theme drawer: it is now styled but still CLOSED. Load its contents, then
    // slide it in only once the product is actually inside — no empty flash, and
    // the button keeps spinning until then.
    getCart().then(function (cart) {
      var inCart = cartVariantIds(cart);
      if (RB_DEBUG) console.debug('[rebuy] selected variant:', wanted, '| cart variants:', inCart);
      var already = inCart.indexOf(wanted) > -1;
      if (already) {
        // Already in the cart (never bump qty): render it and reveal.
        disableBtn(trigger, true);
        renderThemeDrawer(cart);
        applyDiscount(code).then(function (c) { if (c) renderThemeDrawer(c); });
        toast(DUPLICATE_MSG);
        revealDrawerWhenReady(trigger);
        return;
      }
      // New add: add, render the item into the closed drawer, then reveal it.
      // Discount runs in parallel so it never delays the product showing.
      return addVariant(wanted, 1).then(function () {
        disableBtn(trigger, true);
        renderThemeDrawer();
        applyDiscount(code).then(function (c) { if (c) renderThemeDrawer(c); });
        revealDrawerWhenReady(trigger);
      });
    }).catch(function () {
      revealThemeDrawer();            // on error, open anyway...
      setBusy(trigger, false);        // ...and never leave the button spinning
    });
  }

  // Decide whether a click is an enroll CTA we should own.
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    if (!e.target.closest) return;

    var themeBtn = e.target.closest('.Sd_addProduct, .add_to_cart');
    var reploBtn = e.target.closest('[data-replo-add-product-variant-to-cart]');
    var link = e.target.closest('a[href]');

    // The theme's native "Enroll Now" / add-to-cart submit button. We must beat
    // the theme's own delegated handler (cart-ajax.js) so its mini-cart drawer
    // never opens and we show Rebuy instead.
    if (themeBtn) {
      var themeVariant = resolveVariant(themeBtn);
      if (!themeVariant) { openUnresolved(); return; } // let the theme own the add
      e.preventDefault();
      e.stopImmediatePropagation();
      handleEnroll(themeVariant, themeBtn);
      return;
    }

    // A Replo add-to-cart button -> add the page's course.
    if (reploBtn) {
      var variantId = resolveVariant(reploBtn);
      if (!variantId) { openUnresolved(); return; } // let Replo own the add
      e.preventDefault();
      e.stopImmediatePropagation();
      handleEnroll(variantId, reploBtn);
      return;
    }

    // A plain "view cart" / "I'm ready" link (href pathname exactly /cart).
    if (link) {
      var u;
      try { u = new URL(link.href, location.origin); } catch (err) { return; }
      if (u.hostname !== location.hostname) return;                       // external
      if (/checkout|redirect|discount/.test(u.search)) return;           // express-checkout permalink -> leave
      if (u.pathname.replace(/\/+$/, '') !== '/cart') return;            // not a bare /cart link
      e.preventDefault();
      e.stopImmediatePropagation();
      var v = resolveVariant(link);
      if (v) handleEnroll(v, link); else openRebuy();
    }
  }, true); // capture phase: beat Replo's own click handler

  // Remove-from-drawer. The theme's own handler for .sd_mini_removeproduct only
  // binds when the cart-drawer setting is on; we drive the drawer regardless, so
  // we handle removal ourselves. Uses the line-item key (stable) rather than the
  // line number (which shifts as lines are removed).
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var btn = e.target.closest('.sd_mini_removeproduct');
    if (!btn) return;
    e.preventDefault();
    var key = btn.getAttribute('data-key');
    var line = parseInt(btn.getAttribute('data-line'), 10);
    var body = { quantity: 0 };
    if (key) body.id = key; else if (line) body.line = line; else return;
    var row = btn.closest('.media-link');
    if (row) row.style.opacity = '0.5';
    ajax(root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); })
      .then(function (cart) {
        try { if (typeof window.buildCart === 'function') window.buildCart(cart, false); } catch (e2) {}
        updateCount(cart);
      })
      .catch(function () { if (row) row.style.opacity = ''; });
  }, false);

  // ---------------------------------------------------------------------------
  // Layer B — network safety-net for adds we did not take over.
  // Clamp course variants to qty 1 (dedupe), refresh count, open Rebuy.
  // ---------------------------------------------------------------------------
  function isCartAdd(url) {
    return !!url && /\/cart\/add(\.js)?(\?|$)/.test(String(url));
  }
  function isCartChange(url) {
    return !!url && /\/cart\/(change|update|clear)(\.js)?(\?|$)/.test(String(url));
  }

  // Re-sync count + enroll-disable state after a non-add cart mutation, e.g. the
  // shopper removes the course inside the Rebuy drawer -> button re-enables.
  function afterCartChange() {
    getCart().then(updateCount).catch(function () {});
  }

  // Apply the page's enrollment discount to WHATEVER is now in the cart, then
  // re-render so the discounted (marked-out + regular) prices show. Used after
  // adds we did not take over, so an upsell added by any path still gets NCB723.
  function applyPageDiscount() {
    var code = discountOn(null);   // null -> falls back to the page's enroll code
    if (!code) return;
    applyDiscount(code).then(function (c) { if (c) renderThemeDrawer(c); });
  }

  var handling = false; // guard against re-entrancy
  function afterExternalAdd() {
    if (handling) return;
    handling = true;
    openRebuy(); // surface the drawer immediately; content syncs below
    getCart().then(function (cart) {
      var courses = courseVariantSet();
      var updates = {}, clamped = false;
      (cart.items || []).forEach(function (it) {
        if (courses[it.variant_id] && it.quantity > 1) { updates[it.key] = 1; clamped = true; }
      });
      if (clamped) {
        return updateLine(Object.keys(updates)[0], 1).then(function () {
          // set any remaining over-cap lines too
          var keys = Object.keys(updates);
          if (keys.length <= 1) return getCart();
          return Promise.all(keys.slice(1).map(function (k) { return updateLine(k, 1); }))
            .then(function () { return getCart(); });
        }).then(function (c2) {
          toast(DUPLICATE_MSG);
          updateCount(c2);
          refreshRebuy();
          openRebuy();
          applyPageDiscount();
        });
      }
      updateCount(cart);
      refreshRebuy();
      openRebuy();
      applyPageDiscount();
    }).catch(function () {
      openRebuy();
    }).then(function () { handling = false; });
  }

  function headerIsInternal(headers) {
    if (!headers) return false;
    if (typeof headers.get === 'function') return !!headers.get(INTERNAL_HEADER);
    return !!(headers[INTERNAL_HEADER] || headers[INTERNAL_HEADER.toLowerCase()]);
  }

  // --- Patch fetch (Replo + modern add-to-cart) ---
  //
  // This wrapper sits in front of EVERY fetch on the page — including the Rebuy
  // app's own loader, because theme.liquid loads this file (line 5) before
  // content_for_header (line 23). So it must be bulletproof:
  //
  //   1. Call the original with `window` as the receiver. This file is 'use
  //      strict', so a bare fetch(url) call arrives here with `this` undefined;
  //      Safari strictly enforces the Window receiver on fetch and threw a
  //      TypeError, which took Rebuy's bundle down with it. Chrome is lenient,
  //      which is exactly why the drawer only ever broke in Safari.
  //   2. Never throw. If our own bookkeeping fails we still return the real
  //      request, so a bug in here can never break another app's networking.
  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      var url, internal;
      try {
        url = (typeof input === 'string') ? input : (input && input.url);
        internal = (init && headerIsInternal(init.headers)) ||
                   (input && input.headers && headerIsInternal(input.headers));
      } catch (e) {
        return nativeFetch.apply(window, arguments);
      }
      var promise = nativeFetch.apply(window, arguments);
      try {
        if (!internal && isCartAdd(url)) {
          return promise.then(function (resp) {
            if (resp && resp.ok) setTimeout(afterExternalAdd, 0);
            return resp;
          });
        }
        if (!internal && isCartChange(url)) {
          return promise.then(function (resp) {
            if (resp && resp.ok) setTimeout(afterCartChange, 0);
            return resp;
          });
        }
      } catch (e) { /* fall through to the untouched promise */ }
      return promise;
    };
  }

  // --- Patch XHR (the theme's jQuery add-to-cart) ---
  var XHR = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
  if (XHR && XHR.open && XHR.send) {
    var origOpen = XHR.open;
    var origSend = XHR.send;
    XHR.open = function (method, url) {
      this.__rbIsAdd = isCartAdd(url);
      this.__rbIsChange = !this.__rbIsAdd && isCartChange(url);
      return origOpen.apply(this, arguments);
    };
    XHR.send = function () {
      if (this.__rbIsAdd || this.__rbIsChange) {
        var isAdd = this.__rbIsAdd;
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) {
            setTimeout(isAdd ? afterExternalAdd : afterCartChange, 0);
          }
        });
      }
      return origSend.apply(this, arguments);
    };
  }

  // Rebuy locks page scroll with `rebuy-cart-visible` (overflow:hidden!important on
  // html/body) whenever it opens its own cart on add. We replaced its cart with
  // the theme drawer and hid Rebuy's, but the scroll lock still fired and lingered
  // after close. Strip the class the instant it appears — the theme drawer's own
  // lock (side_Drawer_open) is separate and unaffected.
  (function killRebuyScrollLock() {
    function strip() {
      var h = document.documentElement, b = document.body;
      if (!b) return;
      // Rebuy's scroll lock is never wanted — strip it whether open or closed.
      // Guard each change: removing an absent class / rewriting '' must not feed
      // the observer its own mutation (that would loop and starve the page).
      if (h.classList.contains('rebuy-cart-visible')) h.classList.remove('rebuy-cart-visible');
      if (b.classList.contains('rebuy-cart-visible')) b.classList.remove('rebuy-cart-visible');
      // If the theme drawer is NOT open, nothing should lock scroll. This covers
      // every close path — including "remove the last item, then close" — where
      // an inline overflow lock or a leftover class kept the page frozen.
      if (!b.classList.contains('side_Drawer_open')) {
        // Only touch styles that are actually set, so re-writing '' can't feed
        // the observer its own mutation in a loop.
        if (b.style.overflow) b.style.overflow = '';
        if (b.style.touchAction) b.style.touchAction = '';
        if (b.style.position) b.style.position = '';
        if (h.style.overflow) h.style.overflow = '';
      }
    }
    strip();
    try {
      var mo = new MutationObserver(strip);
      mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style'] });
      if (document.body) mo.observe(document.body, { attributes: true, attributeFilter: ['class', 'style'] });
    } catch (e) {}
    // Belt-and-suspenders: a slow tick catches any lock applied without a class
    // change the observer could see.
    setInterval(strip, 400);
  })();

  // On load: sync the per-variant enroll-disable state + badge to the real cart
  // (covers back/forward + bfcache restore). Only buttons for variants actually
  // in the cart get disabled.
  function initSync() {
    // Pull in Rebuy's lazy Smart Cart module now rather than on first click.
    // It only appears once importSmartCartModule() is called, and in Safari
    // nothing else ever calls it — so the drawer is ready before it is needed,
    // and the page loader has something real to wait for.
    ensureSmartCart();
    var warm = 0;
    var warmTimer = setInterval(function () {
      ensureSmartCart();
      if ((window.Rebuy && window.Rebuy.SmartCart) || ++warm > 60) clearInterval(warmTimer);
    }, 200);   // Rebuy itself may still be booting; keep asking for ~12s

    getCart().then(updateCount).catch(function () {});
    if (/[?&]viewcart=true/.test(location.search)) openRebuy();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSync);
  } else {
    initSync();
  }
  window.addEventListener('pageshow', function (e) { if (e.persisted) initSync(); });
})();
